import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useCart from '../hooks/useCart';
import { setQty, removeLine, updateLine, stockFor } from '../lib/cart';
import { EXCHANGE, KIT_ADDON, KIT_COPPER_METRES, BUSINESS, LOW_STOCK_AT } from '../data/pricing';
import { lookupPincode, deliveryCharge, isValidPincode, amountToFreeDelivery } from '../data/pincodes';
import { priceQuote, formatINR } from '../lib/quote';
import { deliveryLabel } from '../lib/deliveryDate';
import { savedPincode, savePincode } from '../lib/customer';
import { IMG_FALLBACK } from '../lib/images';
import { track, FUNNEL } from '../lib/track';
import { nextPriceFor } from '../lib/listing';

// The cart itself, shared by the slide-out drawer and the /cart page so the
// two can never disagree.
//
// It answers everything before checkout: what you are buying, when it
// arrives, what delivery costs, what your old AC is worth, and the total. One
// button then takes you to checkout. Installation is asked once, at checkout,
// for the whole order.

const MAX_UNITS = 6;

// The exchange band for a tonnage: a 1.8 ton AC takes the "2 ton and above" band.
const bandFor = (ton) =>
  (EXCHANGE.copper.find((b) => Number(ton) <= b.maxTon) || EXCHANGE.copper[EXCHANGE.copper.length - 1]);

function Chip({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={on
        ? 'min-h-[40px] rounded-lg border-2 border-blue-600 bg-blue-50 px-3 text-[13px] font-semibold text-blue-800'
        : 'min-h-[40px] rounded-lg border border-gray-300 bg-white px-3 text-[13px] text-gray-700 hover:border-gray-400'}>
      {children}
    </button>
  );
}

function DeliveryRow({ pin, onPin }) {
  const [editing, setEditing] = useState(!pin?.ok);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const check = () => {
    if (!isValidPincode(draft)) { setError('Enter a 6-digit pincode'); return; }
    const r = lookupPincode(draft);
    track(FUNNEL.PINCODE_CHECKED, { pincode: r.pin, serviceable: r.ok, zone: r.zone, source: 'cart' });
    if (!r.ok) { setError(r.message || 'We do not deliver to this pincode yet.'); return; }
    savePincode(r.pin);
    onPin(r.pin);
    setEditing(false);
    setError('');
  };

  if (!editing && pin?.ok) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3">
        <p className="min-w-0 text-sm text-gray-900">
          <span className="font-semibold">Deliver to {pin.pin}</span>
          <span className="text-gray-600"> · by {deliveryLabel()}</span>
        </p>
        <button type="button" onClick={() => { setDraft(''); setEditing(true); }}
          className="shrink-0 text-sm font-semibold text-blue-700">Change</button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-3">
      <label htmlFor="cart-pincode" className="text-sm font-semibold text-gray-900">
        Delivery pincode
      </label>
      <div className="mt-2 flex gap-2">
        <input id="cart-pincode" inputMode="numeric" autoComplete="postal-code" maxLength={6}
          value={draft} placeholder="For delivery date and charge"
          onChange={(e) => { setDraft(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') check(); }}
          className="h-11 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
        <button type="button" onClick={check}
          className="h-11 shrink-0 rounded-lg bg-gray-900 px-4 text-sm font-semibold text-white hover:bg-gray-700">
          Check
        </button>
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-600">
          {error}{' '}
          {isValidPincode(draft) && (
            <a href={`https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(`Hi, do you deliver to pincode ${draft}?`)}`} target="_blank" rel="noopener noreferrer"
              className="font-semibold text-green-700 underline">WhatsApp us</a>
          )}
        </p>
      )}
    </div>
  );
}

function ExchangeBox({ line, raw }) {
  const on = Boolean(raw?.exchange);
  const best = EXCHANGE.copper[EXCHANGE.copper.length - 1].value;

  const toggle = (checked) => {
    if (checked) {
      // Most people replace like for like, so start from the new AC's own
      // size and a copper coil. The chips are right there to correct it.
      updateLine(line.lineId, {
        exchange: true,
        exchangeTon: raw?.exchangeTon || String(bandFor(line.model.soldAs).maxTon),
        exchangeCoil: raw?.exchangeCoil || 'copper',
      });
    } else {
      updateLine(line.lineId, { exchange: false });
    }
  };

  if (!on) {
    return (
      <label className="mt-3 flex cursor-pointer items-center gap-2.5 rounded-xl border border-dashed border-gray-300 px-3 py-2.5 text-[13px] font-semibold text-gray-900">
        <input type="checkbox" checked={false} onChange={(e) => toggle(e.target.checked)}
          className="h-5 w-5 rounded border-gray-300 text-blue-600" />
        Exchange an old AC
        <span className="ml-auto text-blue-700">up to {formatINR(best)} off</span>
      </label>
    );
  }

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <label className="flex cursor-pointer items-center gap-2.5 text-[13px] font-semibold text-gray-900">
        <input type="checkbox" checked onChange={(e) => toggle(e.target.checked)}
          className="h-5 w-5 rounded border-gray-300 text-blue-600" />
        Exchange {line.qty > 1 ? `${line.qty} old ACs` : 'an old AC'}
        <span className="ml-auto text-green-700">
          {line.exchangeAmount > 0 ? <>&minus; {formatINR(line.exchangeAmount)}</> : 'Choose below'}
        </span>
      </label>
      <div>
        <p className="mb-1.5 text-[12px] font-medium text-gray-700">Old AC capacity</p>
        <div className="flex flex-wrap gap-1.5">
          {EXCHANGE.copper.map((b) => (
            <Chip key={b.label} on={String(raw?.exchangeTon) === String(b.maxTon)}
              onClick={() => updateLine(line.lineId, { exchangeTon: String(b.maxTon) })}>
              {b.label}
            </Chip>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-[12px] font-medium text-gray-700">Coil</p>
        <div className="flex flex-wrap gap-1.5">
          <Chip on={raw?.exchangeCoil === 'copper'} onClick={() => updateLine(line.lineId, { exchangeCoil: 'copper' })}>
            Copper coil
          </Chip>
          <Chip on={raw?.exchangeCoil === 'aluminium'} onClick={() => updateLine(line.lineId, { exchangeCoil: 'aluminium' })}>
            Aluminium coil
          </Chip>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500">
          Copper pipes look reddish-brown, aluminium is silver-grey. Free uninstallation, any brand.
        </p>
      </div>
    </div>
  );
}

export default function CartContents({ variant = 'drawer', onClose }) {
  const lines = useCart();
  const navigate = useNavigate();
  const [pincode, setPincode] = useState(() => savedPincode());

  const pin = useMemo(() => (pincode ? lookupPincode(pincode) : null), [pincode]);
  const base = useMemo(() => priceQuote(lines), [lines]);
  const delivery = useMemo(
    () => (pin?.ok ? deliveryCharge(pin, { orderValue: base.equipment, units: base.units }) : null),
    [pin, base.equipment, base.units],
  );
  const quote = useMemo(() => priceQuote(lines, { delivery }), [lines, delivery]);
  const raws = useMemo(() => Object.fromEntries(lines.map((l) => [l.lineId, l])), [lines]);
  const gap = amountToFreeDelivery(base.equipment);
  const freeAbove = amountToFreeDelivery(0);

  if (!lines.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <p className="text-base font-semibold text-gray-900">Your cart is empty</p>
        <p className="mt-1 text-sm text-gray-600">Every Daikin AC we stock, with today&apos;s price.</p>
        <Link to="/daikin-ac-price-list-delhi" onClick={onClose}
          className="mt-5 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700">
          Browse Daikin ACs
        </Link>
      </div>
    );
  }

  const tooMany = quote.units > MAX_UNITS;

  const goCheckout = () => {
    track(FUNNEL.CHECKOUT_STARTED, { value: quote.total, units: quote.units, source: variant });
    if (onClose) onClose();
    navigate('/checkout');
  };

  const body = (
    <div className="space-y-3">
      <DeliveryRow pin={pin} onPin={setPincode} />

      {quote.lines.map((l) => {
        const raw = raws[l.lineId];
        const canAddKit = l.kit === 'withoutKit' && KIT_ADDON.available;
        return (
          <article key={l.lineId} className="rounded-xl border border-gray-200 bg-white p-3.5">
            <div className="flex gap-3">
              <img src={l.model.image} alt="" width="72" height="72"
                onError={(e) => { e.currentTarget.src = IMG_FALLBACK; }}
                className="h-[72px] w-[72px] shrink-0 rounded-lg bg-gray-50 object-contain p-1" />
              <div className="min-w-0 flex-1">
                <Link to={`/${l.model.slug}?kit=${l.kit}`} onClick={onClose}
                  className="block text-[14px] font-semibold leading-snug text-gray-900 hover:text-blue-700">
                  {l.model.title}
                </Link>
                <p className="mt-0.5 text-[12px] text-gray-600">
                  {l.model.variants[l.kit]?.idu} · {l.kit === 'withKit' ? `With kit, ${KIT_COPPER_METRES} m copper` : 'Without kit'}
                </p>
                <p className="mt-0.5 text-[12px] font-semibold text-green-700">Delivery by {deliveryLabel()}</p>
                {nextPriceFor(l.model.id, l.kit) ? (
                  <>
                    <p className="mt-0.5 text-[12px] font-semibold text-red-700">
                      {stockFor(l.model.id, l.kit) === 1
                        ? `Last unit at ${formatINR(l.unitPrice)}`
                        : `Only ${stockFor(l.model.id, l.kit)} left at ${formatINR(l.unitPrice)}`}
                    </p>
                    <p className="text-[12px] text-stone-600">
                      The next one costs {formatINR(nextPriceFor(l.model.id, l.kit))}. You save {formatINR((nextPriceFor(l.model.id, l.kit) - l.unitPrice) * l.qty)}.
                    </p>
                  </>
                ) : l.qty >= stockFor(l.model.id, l.kit) && stockFor(l.model.id, l.kit) <= LOW_STOCK_AT && (
                  <p className="mt-0.5 text-[12px] font-semibold text-red-700">
                    {stockFor(l.model.id, l.kit) === 1 ? 'Last unit in stock' : `Only ${stockFor(l.model.id, l.kit)} in stock`}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex h-10 items-center rounded-lg border border-gray-300">
                <button type="button" onClick={() => setQty(l.lineId, l.qty - 1)}
                  aria-label={l.qty === 1 ? 'Remove' : 'One fewer'}
                  className="h-full w-10 text-lg text-gray-700">−</button>
                <span className="w-7 text-center text-sm font-semibold tabular-nums">{l.qty}</span>
                <button type="button" onClick={() => setQty(l.lineId, l.qty + 1)} aria-label="One more"
                  disabled={l.qty >= stockFor(l.model.id, l.kit)}
                  className="h-full w-10 text-lg text-gray-700 disabled:opacity-30">+</button>
              </div>
              <div className="text-right">
                <p className="text-base font-bold tabular-nums text-gray-900">{formatINR(l.equipment)}</p>
                {l.mrp > l.unitPrice * l.qty && (
                  <p className="text-[12px] text-gray-500 line-through tabular-nums">{formatINR(l.mrp)}</p>
                )}
              </div>
            </div>

            {canAddKit && (
              <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-[13px] text-gray-800">
                <input type="checkbox" checked={Boolean(raw?.kitAddon)}
                  onChange={(e) => { updateLine(l.lineId, { kitAddon: e.target.checked }); track(FUNNEL.KIT_ADDON_TOGGLED, { model: l.model.id, added: e.target.checked }); }}
                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600" />
                <span>
                  Add the {KIT_ADDON.label} <span className="font-semibold">+{formatINR(KIT_ADDON.price)}</span>
                  <span className="mt-0.5 block text-[11px] text-gray-500">{KIT_ADDON.note}</span>
                </span>
              </label>
            )}

            {EXCHANGE.enabled && <ExchangeBox line={l} raw={raw} />}

            <button type="button" onClick={() => removeLine(l.lineId)}
              className="mt-2 py-1 text-[13px] text-gray-500 underline underline-offset-2 hover:text-red-600">
              Remove
            </button>
          </article>
        );
      })}

      {gap && freeAbove && (
        <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-3">
          <p className="text-[13px] font-medium text-gray-700">
            Add {formatINR(gap)} more for <span className="font-bold text-green-700">free delivery</span>
          </p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.min(100, Math.round((base.equipment / freeAbove) * 100))}%` }} />
          </div>
        </div>
      )}

      <dl className="rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-sm">
        <div className="flex justify-between py-1 text-gray-800">
          <dt>Items ({quote.units})</dt><dd className="tabular-nums">{formatINR(quote.equipment)}</dd>
        </div>
        <div className="flex justify-between py-1 text-gray-800">
          <dt>Delivery</dt>
          <dd className="tabular-nums">{!delivery ? <span className="text-gray-500">Enter pincode</span> : delivery.free ? <span className="font-semibold text-green-700">Free</span> : formatINR(delivery.price)}</dd>
        </div>
        {quote.exchangeTotal > 0 && (
          <div className="flex justify-between py-1 font-medium text-green-700">
            <dt>Old AC exchange credit</dt><dd className="tabular-nums">&minus; {formatINR(quote.exchangeTotal)}</dd>
          </div>
        )}
        <div className="mt-1 flex justify-between border-t border-dashed border-gray-300 pt-2 text-base font-bold text-gray-900">
          <dt>Total</dt><dd className="tabular-nums">{formatINR(quote.total)}</dd>
        </div>
        {quote.savings > 0 && (
          <p className="mt-1 text-[12px] font-semibold text-green-700">You save {formatINR(quote.savings)} on MRP</p>
        )}
      </dl>

      {tooMany && (
        <p className="rounded-xl bg-blue-50 px-3.5 py-3 text-[13px] text-blue-900">
          More than {MAX_UNITS} units is priced as a project. Call {BUSINESS.phone} and we&apos;ll quote it properly.
        </p>
      )}
    </div>
  );

  const footer = (
    <div className={variant === 'drawer'
      ? 'border-t border-gray-200 bg-white px-4 pb-4 pt-3'
      : 'sticky bottom-0 -mx-4 border-t border-gray-200 bg-white px-4 pb-4 pt-3 sm:static sm:mx-0 sm:border-0 sm:px-0'}>
      <button type="button" onClick={goCheckout} disabled={tooMany}
        className="h-[52px] w-full rounded-xl bg-blue-600 text-base font-bold text-white transition hover:bg-blue-700 disabled:opacity-50">
        Checkout · {formatINR(quote.total)}
      </button>
      {variant === 'drawer' ? (
        <button type="button" onClick={onClose}
          className="mt-1.5 h-9 w-full text-sm font-medium text-gray-600 hover:text-gray-900">
          Continue shopping
        </button>
      ) : (
        <Link to="/daikin-ac-price-list-delhi"
          className="mt-1.5 block py-2 text-center text-sm font-medium text-gray-600 hover:text-gray-900">
          Continue shopping
        </Link>
      )}
    </div>
  );

  if (variant === 'drawer') {
    return (
      <>
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-4 py-3">{body}</div>
        {footer}
      </>
    );
  }
  return (
    <>
      {body}
      {footer}
    </>
  );
}
